import { useState, useEffect } from "react";
import { HasAccess, useHasAccess } from "@/components/common/has-access";
import { format } from "date-fns";

function buildDocUrl(value: string): string {
  const trimmed = value.trim();
  if (trimmed.startsWith("http")) return trimmed;
  return `https://docs.google.com/document/d/${trimmed}`;
}

interface DocConflict {
  docKey: string;
  sources: { name: string; url: string }[];
  target: { name: string; url: string };
}
import VaulDrawer from "@/components/ui/drawer";
import ThreeDotsLoader from "@/components/common/three-dots-loader";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  UserIcon,
  MailIcon,
  PhoneIcon,
  CalendarIcon,
  MergeIcon,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  DuplicateIdentifierGroup,
  DuplicateUserSummary,
} from "@/pages/users/types/user";
import { useMergeDuplicateUsers } from "../hooks/use-merge-duplicate-users";

interface DuplicateAccountsSheetProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  groups: DuplicateIdentifierGroup[];
  isLoading: boolean;
  onUserClick: (userId: string) => void;
}

function UserCard({
  user,
  onClick,
}: {
  user: DuplicateUserSummary;
  onClick: () => void;
}) {
  const isActive = user.has_identifiers;

  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left p-3 rounded-md border transition-colors",
        isActive
          ? "border-green-200 bg-green-50 hover:border-green-300 hover:bg-green-100"
          : "border-gray-100 hover:border-gray-300 hover:bg-gray-50"
      )}
    >
      <div className="flex items-start gap-3">
        <div
          className={cn(
            "mt-0.5 w-7 h-7 rounded-full flex items-center justify-center shrink-0",
            isActive ? "bg-green-100" : "bg-gray-100"
          )}
        >
          <UserIcon
            className={cn(
              "w-3.5 h-3.5",
              isActive ? "text-green-600" : "text-gray-500"
            )}
          />
        </div>
        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="text-sm font-medium text-gray-900 truncate">
              {user.full_name}
            </p>
            {isActive && (
              <span className="inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800 shrink-0">
                Currently Used
              </span>
            )}
          </div>
          {user.preferred_email && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <MailIcon className="w-3 h-3 shrink-0" />
              <span className="truncate">{user.preferred_email}</span>
            </div>
          )}
          {user.preferred_phone_number && (
            <div className="flex items-center gap-1.5 text-xs text-gray-500">
              <PhoneIcon className="w-3 h-3 shrink-0" />
              <span>{user.preferred_phone_number}</span>
            </div>
          )}
          <div className="flex items-center gap-1.5 text-xs text-gray-400">
            <CalendarIcon className="w-3 h-3 shrink-0" />
            <span>
              Created {format(new Date(user.created_at), "dd MMM yyyy")}
            </span>
          </div>
        </div>
      </div>
    </button>
  );
}

function IdentifierGroup({
  group,
  isSelected,
  onToggle,
  onUserClick,
}: {
  group: DuplicateIdentifierGroup;
  isSelected: boolean;
  onToggle: () => void;
  onUserClick: (userId: string) => void;
}) {
  const hasTarget = group.users.some((u) => u.has_identifiers);
  // Ticking a group selects it for a merge — a write — so view-only has no box.
  const canWrite = useHasAccess("write");

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2 py-1">
        {canWrite && (
          <Checkbox
            checked={isSelected}
            onCheckedChange={onToggle}
          />
        )}
        <span className="text-xs font-mono text-gray-400 bg-gray-50 border border-gray-200 rounded px-2 py-0.5 truncate max-w-[220px]">
          {group.otpless_identifier}
        </span>
        <Badge variant="secondary" className="shrink-0 text-xs">
          {group.users.length} accounts
        </Badge>
        {!hasTarget && (
          <span className="text-xs text-amber-600 shrink-0">
            No active account — latest will be target
          </span>
        )}
      </div>
      <div className="pl-6 space-y-1.5 border-l-2 border-yellow-300">
        {group.users.map((user) => (
          <UserCard
            key={user.id}
            user={user}
            onClick={() => onUserClick(user.id)}
          />
        ))}
      </div>
    </div>
  );
}

export function DuplicateAccountsSheet({
  isOpen,
  onOpenChange,
  groups,
  isLoading,
  onUserClick,
}: DuplicateAccountsSheetProps) {
  const [selectedIdentifiers, setSelectedIdentifiers] = useState<Set<string>>(
    new Set()
  );
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const { mutate: mergeUsers, isPending: isMerging } = useMergeDuplicateUsers();

  // Reset selection when groups change (new search result)
  useEffect(() => {
    setSelectedIdentifiers(new Set());
  }, [groups]);

  const mergeableGroups = groups;
  const allMergeableSelected =
    mergeableGroups.length > 0 &&
    mergeableGroups.every((g) => selectedIdentifiers.has(g.otpless_identifier));
  const someSelected = selectedIdentifiers.size > 0;

  const toggleGroup = (id: string) => {
    setSelectedIdentifiers((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allMergeableSelected) {
      setSelectedIdentifiers(new Set());
    } else {
      setSelectedIdentifiers(
        new Set(mergeableGroups.map((g) => g.otpless_identifier))
      );
    }
  };

  const resolveTarget = (g: DuplicateIdentifierGroup): DuplicateUserSummary =>
    g.users.find((u) => u.has_identifiers) ??
    [...g.users].sort(
      (a, b) =>
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    )[0];

  const buildMergePayload = () =>
    groups
      .filter((g) => selectedIdentifiers.has(g.otpless_identifier))
      .map((g) => {
        const target = resolveTarget(g);
        return {
          target_user_id: target.id,
          source_user_ids: g.users
            .filter((u) => u.id !== target.id)
            .map((u) => u.id),
        };
      });

  const docConflictMap = new Map<string, DocConflict>();
  groups
    .filter((g) => selectedIdentifiers.has(g.otpless_identifier))
    .forEach((g) => {
      const target = resolveTarget(g);
      const sources = g.users.filter((u) => u.id !== target.id);
      if (!target.document_ids) return;
      for (const source of sources) {
        if (!source.document_ids) continue;
        for (const key of Object.keys(source.document_ids)) {
          if (target.document_ids[key]) {
            if (!docConflictMap.has(key)) {
              docConflictMap.set(key, {
                docKey: key,
                sources: [],
                target: {
                  name: target.full_name,
                  url: buildDocUrl(target.document_ids[key]),
                },
              });
            }
            docConflictMap.get(key)!.sources.push({
              name: source.full_name,
              url: buildDocUrl(source.document_ids[key]),
            });
          }
        }
      }
    });
  const docConflicts = Array.from(docConflictMap.values());

  const handleConfirmMerge = () => {
    const payload = buildMergePayload();
    mergeUsers(payload, {
      onSuccess: () => {
        setIsConfirmOpen(false);
        setSelectedIdentifiers(new Set());
      },
    });
  };

  return (
    <>
      <VaulDrawer
        drawerDirection="right"
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        triggerChildren={null}
        customClass="w-[480px] h-full bg-black"
        contentChildren={
          <div className="h-[calc(100vh-2rem)] w-full flex flex-col">
            {/* Header */}
            <div className="px-6 pt-6 pb-4 border-b border-gray-100 shrink-0">
              <div className="flex items-center gap-3">
                <div>
                  <h2 className="text-base font-semibold text-gray-900">
                    Duplicate Accounts
                  </h2>
                  <p className="text-xs text-gray-500 mt-0.5">
                    Users sharing the same OTPless identifier
                  </p>
                </div>
                {!isLoading && groups.length > 0 && (
                  <Badge className="ml-auto shrink-0 bg-yellow-100 text-yellow-800 border-yellow-200">
                    {groups.length} identifier{groups.length !== 1 ? "s" : ""}
                  </Badge>
                )}
              </div>
            </div>

            {/* Subheader — shown when any group is selected */}
            {someSelected && (
              <div className="px-6 py-2.5 border-b border-gray-100 bg-gray-50 shrink-0 flex items-center gap-3">
                <span className="text-xs text-gray-600 font-medium">
                  {selectedIdentifiers.size} selected
                </span>
                <button
                  onClick={toggleAll}
                  className="text-xs text-blue-600 hover:underline"
                >
                  {allMergeableSelected ? "Deselect all" : "Select all"}
                </button>
                <div className="ml-auto">
                  <HasAccess permission="write">
                  <Button
                    size="sm"
                    onClick={() => setIsConfirmOpen(true)}
                    disabled={isMerging}
                    className="h-7 text-xs gap-1.5"
                  >
                    <MergeIcon className="w-3.5 h-3.5" />
                    Merge
                  </Button>
                  </HasAccess>
                </div>
              </div>
            )}

            {/* Body */}
            {isLoading ? (
              <ThreeDotsLoader />
            ) : groups.length === 0 ? (
              <div className="flex flex-col items-center justify-center flex-1 text-center px-6 py-12">
                <p className="text-sm text-gray-500">
                  No duplicate identifiers found in the current search results.
                </p>
              </div>
            ) : (
              <ScrollArea className="flex-1">
                <div className="px-6 py-4 space-y-6">
                  {groups.map((group) => (
                    <IdentifierGroup
                      key={group.otpless_identifier}
                      group={group}
                      isSelected={selectedIdentifiers.has(
                        group.otpless_identifier
                      )}
                      onToggle={() => toggleGroup(group.otpless_identifier)}
                      onUserClick={(userId) => {
                        onOpenChange(false);
                        onUserClick(userId);
                      }}
                    />
                  ))}
                </div>
              </ScrollArea>
            )}
          </div>
        }
      />

      {/* Merge confirmation dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Merge</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 text-sm text-gray-600">
            <p>
              You are about to merge{" "}
              <span className="font-semibold text-gray-900">
                {selectedIdentifiers.size} group
                {selectedIdentifiers.size !== 1 ? "s" : ""}
              </span>
              .
            </p>
            <p>
              Bundles, notes, prescriptions and invoices will be transferred to
              the{" "}
              <span className="font-semibold text-green-700">
                Currently Used
              </span>{" "}
              account. The source accounts' OTPless identifier will be cleared.
            </p>
            {docConflicts.length > 0 && (
              <div className="rounded-md bg-amber-50 border border-amber-200 p-3 space-y-3">
                <p className="text-xs font-semibold text-amber-900">
                  Merge Conflicts:
                </p>
                {docConflicts.map((c) => (
                  <div key={c.docKey} className="space-y-1">
                    <p className="text-xs font-medium text-amber-800">
                      {c.docKey} Doc
                    </p>
                    <ul className="pl-3 space-y-0.5">
                      {c.sources.map((s, i) => (
                        <li
                          key={i}
                          className="text-xs text-amber-700 flex items-center gap-1"
                        >
                          <span className="shrink-0">– Old Account:</span>
                          <a
                            href={s.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="underline hover:text-amber-900 truncate"
                          >
                            {s.name}
                          </a>
                        </li>
                      ))}
                      <li className="text-xs text-amber-700 flex items-center gap-1">
                        <span className="shrink-0">– Authorised Account:</span>
                        <a
                          href={c.target.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline hover:text-amber-900 truncate"
                        >
                          {c.target.name}
                        </a>
                      </li>
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <p className="text-amber-700 font-medium">
              This action cannot be undone.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isMerging}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmMerge} disabled={isMerging}>
              {isMerging ? "Merging…" : "Merge"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
